import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { pdApi, type PdOpportunity } from '../api';
import '../screens.css';
import { HelpPanel } from '../HelpPanel';
import { SCORING_QUEUE, SCORING_IN_PROGRESS, SCORING_COMPLETED } from '../helpContent';
import { SCORING_OVERVIEW } from '../helpContent';

/**
 * Opportunities → Scoring, Scoring Queue, In Progress and Completed.
 *
 * Cloned from the source's four screens: `opportunities-scoring.ejs`,
 * `opportunities-scoring-queue.ejs`, `opportunities-scoring-in-progress.ejs` and
 * `opportunities-scoring-completed.ejs`.
 *
 * THE THREE QUEUES ARE NOT SIMPLY THREE STATUSES, and the difference matters:
 *
 *   * Queue holds anything Ready for Scoring — PLUS anything already In Progress with no
 *     answers yet. A scoring run that was opened and abandoned belongs back in the queue,
 *     not in the in-progress list.
 *   * In Progress holds only rows with at least one answer.
 *   * Completed holds rows where every question is answered, not merely rows marked
 *     Scored — so an opportunity whose framework grew after it was scored drops off.
 *
 * RE-SCORE IS BLOCKED once effort planning exists: downstream planning is built on the
 * score that a re-score would discard.
 */

const CARDS = [
  { key: 'queue', label: 'In Queue', sub: 'Ready for scoring', colour: '#2196F3' },
  { key: 'in-progress', label: 'In Progress', sub: 'Currently being scored', colour: '#FF9800' },
  { key: 'completed', label: 'Completed', sub: 'Scoring completed', colour: '#4CAF50' },
  { key: 'heatmap', label: 'Heat Map', sub: 'Visual score comparison', colour: '#009688' },
] as const;

const BASE = '/portfolio_definition/opportunities';

export function ScoringOverview() {
  const navigate = useNavigate();
  const [stats, setStats] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void (async () => {
      try {
        const data = await pdApi.scoring.overview();
        setStats(data.stats);
      } catch (e) {
        setError((e as Error).message);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // The heat map draws on the same completed set, so it shows that count.
  const value = (key: string) =>
    key === 'queue'
      ? (stats.queue_count ?? 0)
      : key === 'in-progress'
        ? (stats.in_progress_count ?? 0)
        : (stats.completed_count ?? 0);

  return (
    <div className="pd-screen">
      <div className="pd-page-header">
        <div>
          <h1 className="pd-page-header__title">Scoring Overview</h1>
          <p className="pd-page-header__subtitle">Manage opportunity scoring workflow</p>
        </div>
        <div className="pd-page-header__actions">
          <HelpPanel help={SCORING_OVERVIEW} />
        </div>
      </div>

      {error ? <div className="pd-error">{error}</div> : null}

      {loading ? (
        <div className="pd-loading">Loading…</div>
      ) : (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
            gap: 14,
          }}
        >
          {CARDS.map((c) => (
            <button
              key={c.key}
              type="button"
              className="pd-threshold-card"
              style={{
                ['--pd-swatch' as string]: c.colour,
                textAlign: 'left',
                cursor: 'pointer',
                font: 'inherit',
              }}
              onClick={() =>
                navigate(
                  c.key === 'queue'
                    ? `${BASE}/scoring-queue`
                    : c.key === 'heatmap'
                      ? `${BASE}/scoring/heatmap`
                      : `${BASE}/${c.key}`,
                )
              }
            >
              <div style={{ fontSize: 30, fontWeight: 800, fontVariantNumeric: 'tabular-nums' }}>
                {value(c.key)}
              </div>
              <div style={{ fontSize: 15, fontWeight: 700, marginTop: 4 }}>{c.label}</div>
              <div style={{ fontSize: 12.5, color: '#64748b', marginTop: 2 }}>{c.sub}</div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/** One list, three modes — the three screens differ only in source and columns. */
export function ScoringQueue({ mode }: { mode: 'queue' | 'in-progress' | 'completed' }) {
  const navigate = useNavigate();
  const [rows, setRows] = useState<PdOpportunity[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data =
        mode === 'queue'
          ? await pdApi.scoring.queue()
          : mode === 'in-progress'
            ? await pdApi.scoring.inProgress()
            : await pdApi.scoring.completed();
      setRows(data.opportunities);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, [mode]);

  useEffect(() => {
    void load();
  }, [load]);

  const title =
    mode === 'queue' ? 'Scoring Queue' : mode === 'in-progress' ? 'Scoring In Progress' : 'Completed Scoring';
  const empty =
    mode === 'queue'
      ? 'Nothing is waiting to be scored.'
      : mode === 'in-progress'
        ? 'No opportunity is part-way through scoring.'
        : 'No opportunity has been fully scored yet.';

  const rescore = async (id: string) => {
    if (!window.confirm(`Re-score ${id}? The existing score is cleared; the answers are kept.`)) {
      return;
    }
    setError(null);
    setNotice(null);
    try {
      await pdApi.scoring.rescore(id);
      setNotice(`${id} moved back to Scoring in Progress.`);
      await load();
    } catch (e) {
      setError((e as Error).message);
    }
  };

  return (
    <div className="pd-screen">
      <div className="pd-page-header">
        <div>
          <h1 className="pd-page-header__title">{title}</h1>
          <p className="pd-page-header__subtitle">
            {mode === 'queue'
              ? 'Opportunities marked Ready for Scoring, waiting for an evaluator to pick them up.'
              : mode === 'in-progress'
                ? 'Opportunities currently being scored across their assessment dimensions.'
                : 'Fully scored opportunities with a recommendation — ready for an approval decision.'}
          </p>
        </div>
        <div className="pd-page-header__actions">
          <span className="pd-count">{rows.length} opportunities</span>
          {/* Three screens off one `mode` prop, so the panel follows it — the source ships
              a separate help object for the queue, in-progress and completed views. */}
          <HelpPanel
            help={
              mode === 'completed'
                ? SCORING_COMPLETED
                : mode === 'in-progress'
                  ? SCORING_IN_PROGRESS
                  : SCORING_QUEUE
            }
          />
        </div>
      </div>

      {error ? <div className="pd-error">{error}</div> : null}
      {notice ? <div className="pd-notice">{notice}</div> : null}

      {loading ? (
        <div className="pd-loading">Loading…</div>
      ) : (
        <div className="pd-table-wrap">
          <table className="pd-table">
            <thead>
              <tr>
                <th style={{ width: 170 }}>Opportunity ID</th>
                <th style={{ width: 320 }}>Description</th>
                <th style={{ width: 150 }}>Project</th>
                <th style={{ width: 130 }}>Framework</th>
                {mode !== 'queue' ? <th style={{ width: 120 }}>Answered</th> : null}
                {mode === 'completed' ? <th style={{ width: 90 }}>Score</th> : null}
                {mode === 'completed' ? <th style={{ width: 200 }}>Recommendation</th> : null}
                <th style={{ width: 170 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={8} className="pd-empty">
                    {empty}
                  </td>
                </tr>
              ) : (
                rows.map((o) => (
                  <tr key={o.opportunity_id}>
                    <td>{o.opportunity_id}</td>
                    <td>{o.description}</td>
                    <td>{o.project_id ?? '—'}</td>
                    <td>{(o.framework_id as string) ?? '—'}</td>
                    {mode !== 'queue' ? (
                      <td className="pd-num">
                        {String(o.answered_questions ?? 0)}
                        {o.total_questions ? ` / ${String(o.total_questions)}` : ''}
                      </td>
                    ) : null}
                    {mode === 'completed' ? (
                      <td className="pd-num">
                        {o.overall_score === null || o.overall_score === undefined
                          ? '—'
                          : Number(o.overall_score).toFixed(2)}
                      </td>
                    ) : null}
                    {mode === 'completed' ? <td>{o.recommendation ?? '—'}</td> : null}
                    <td>
                      <div className="pd-row-actions">
                        {mode === 'completed' ? (
                          <button
                            type="button"
                            className="pd-btn"
                            disabled={Boolean(o.has_effort)}
                            title={
                              o.has_effort
                                ? 'Cannot re-score: effort planning records exist for this opportunity'
                                : undefined
                            }
                            onClick={() => rescore(o.opportunity_id)}
                          >
                            Re-score
                          </button>
                        ) : (
                          <button
                            type="button"
                            className="pd-btn pd-btn--primary"
                            onClick={() =>
                              navigate(
                                `${BASE}/scoring-queue/${encodeURIComponent(o.opportunity_id)}`,
                              )
                            }
                          >
                            {mode === 'queue' ? 'Start scoring' : 'Continue'}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
